import { useState } from 'react';
import { Button, Flex, Form, Input, Typography } from 'antd';
import type { FormProps } from 'antd';
import { Link, useNavigate } from 'react-router-dom';
import { AnimatePresence, motion } from 'framer-motion';
import { useTranslation } from 'react-i18next';

// services
import NotificationService from 'core/services/notification';
import { checkPhoneValidation } from 'utils/validators/phone';

// components
import PhoneInputComponent, { PhoneInputReturn } from 'components/phoneInput/phoneInput';
import LanguageButton from 'components/language-button/language-button';
import OTP from 'components/otp/otp';
import SuccessComponent from 'components/success/success';
import PageTitle from 'components/title/title';

// styles
import styles from './reset-password.module.scss';
import darkLogo from 'assets/images/logo-dark.svg';
import FormPassword from 'components/formPassword/formPassword';
import FormElement from 'components/formElement/formElement';

function BusinessResetPasswordPage() {
	const navigate = useNavigate();
	const { t } = useTranslation();

	const [emailForm] = Form.useForm();
	const [passwordForm] = Form.useForm();

	const [isLoading, setIsLoading] = useState(false);
	const [currentStep, setCurrentStep] = useState<'email' | 'otp' | 'password' | 'success'>('email');

	type emailFieldType = {
		email: string;
	};

	type passwordFieldType = {
		password: string;
		confirmPassword: string;
	};

	const validatePassword = (_: any, value: string) => {
		if (!value || passwordForm.getFieldValue('password') === value) {
			return Promise.resolve();
		}
		return Promise.reject(new Error(t('COMMON.PASSWORDS_NOT_MATCH')));
	};

	const onEmailFinish: FormProps<emailFieldType>['onFinish'] = async (values) => {
		try {
			setIsLoading(true);

			setTimeout(() => {
				setIsLoading(false);
				setCurrentStep('otp');
			}, 1000);
		} catch (error: any) {
			NotificationService.error('Ops !!', 'Error');
		}
	};

	const onOtpFinish = async (otp: string) => {
		try {
			setIsLoading(true);

			setTimeout(() => {
				setIsLoading(false);
				setCurrentStep('password');
			}, 1000);
		} catch (error: any) {
			NotificationService.error('Ops !!', 'Error');
		}
	};

	const onPasswordFinish: FormProps<passwordFieldType>['onFinish'] = async (values) => {
		try {
			setIsLoading(true);

			setTimeout(() => {
				setIsLoading(false);
				setCurrentStep('success');

				setTimeout(() => {
					navigate('/business/login');
				}, 5000);
			}, 1000);
		} catch (error: any) {
			setIsLoading(false);
			NotificationService.error('Ops !!', 'Error');
		}
	};

	return (
		<>
			<PageTitle name={t('TITLE.RESET_PASSWORD')} />

			<Flex vertical style={{ minHeight: '100vh', width: '100vw' }}>
				<motion.div
					initial={{ opacity: 0, transform: 'translateY(-100%)' }}
					animate={{ opacity: 1, transform: 'translateY(0%)' }}
					transition={{ duration: 0.5 }}>
					<header className="p-3 p-md-5 d-flex justify-content-between align-items-center">
						<Link to="/login">
							<img style={{ width: 200 }} src={darkLogo} />
						</Link>

						<LanguageButton />
					</header>
				</motion.div>

				<main className="mt-5" style={{ minHeight: 320 }}>
					{/* phone number div */}
					{currentStep === 'email' && (
						<motion.div
							initial={{ opacity: 0, transform: 'translateY(-50px)' }}
							animate={{ opacity: 1, transform: 'translateY(0px)' }}
							exit={{ opacity: 0, transform: 'translateY(-50px)' }}
							transition={{ delay: 0.3, duration: 0.5 }}
							key="emailStep">
							<div className={styles.phoneDiv}>
								<div className="text-center mb-5">
									<h2>{t('RESET_PASSWORD.REST_YOUR_PASSWORD')}</h2>
									<p className="text-light">{t('RESET_PASSWORD.FILL_TO_RESET')}</p>
								</div>

								<Form form={emailForm} onFinish={onEmailFinish}>
									<FormElement
										name="email"
										type="email"
										label={t('COMMON.EMAIL')}
										rules={[{ required: true, message: t('COMMON.EMAIL_REQUIRED') }]}
									/>

									<Form.Item>
										<Button block type="primary" htmlType="submit" loading={isLoading}>
											{t('COMMON.RESET')}
										</Button>
									</Form.Item>
								</Form>
							</div>
						</motion.div>
					)}

					{/* OTP div */}
					{currentStep === 'otp' && (
						<motion.div
							initial={{ opacity: 0, transform: 'translateY(-50px)' }}
							animate={{ opacity: 1, transform: 'translateY(0px)' }}
							exit={{ opacity: 0, transform: 'translateY(-50px)' }}
							transition={{ delay: 0.3, duration: 0.5 }}
							key="otpStep">
							<div className={styles.otpDiv}>
								<h3 className={`mb-5`}>{t('RESET_PASSWORD.VERIFY_PHONE')}</h3>

								<OTP isLoading={isLoading} onConfirm={onOtpFinish} ttl={60} onResend={() => {}} />
							</div>
						</motion.div>
					)}

					{/* new password div */}
					{currentStep === 'password' && (
						<motion.div
							initial={{ opacity: 0, transform: 'translateY(-50px)' }}
							animate={{ opacity: 1, transform: 'translateY(0px)' }}
							exit={{ opacity: 0, transform: 'translateY(-50px)' }}
							transition={{ delay: 0.3, duration: 0.5 }}
							key="passwordStep">
							<div className={styles.passwordDiv}>
								<h3 className={`mb-5 text-center`}>{t('RESET_PASSWORD.ENTER_PASSWORD')}</h3>
								<Form form={passwordForm} onFinish={onPasswordFinish}>
									<FormPassword
										fullWidth
										name="password"
										label={t('COMMON.PASSWORD')}
										rules={[
											{ required: true, message: t('COMMON.PASSWORD_REQUIRED') },
											{ min: 8, message: t('COMMON.PASSWORD_MIN_8') },
										]}
									/>

									<div className="my-4">
										<FormPassword
											fullWidth
											name="confirmPassword"
											label={t('COMMON.PASSWORD_CONFIRMATION')}
											rules={[{ required: true, message: t('COMMON.PASSWORD_CONFIRMATION_REQUIRED') }, { validator: validatePassword }]}
										/>
									</div>

									<Form.Item>
										<Button block type="primary" htmlType="submit" loading={isLoading}>
											{t('RESET_PASSWORD.RESET_PASSWORD')}
										</Button>
									</Form.Item>
								</Form>
							</div>
						</motion.div>
					)}

					{/* success div */}
					{currentStep === 'success' && (
						<div className={styles.successDiv}>
							<motion.div
								initial={{ opacity: 0, transform: 'translateY(-50px)' }}
								animate={{ opacity: 1, transform: 'translateY(0px)' }}
								transition={{ delay: 0.3, duration: 0.5 }}>
								<SuccessComponent />
								<h5 className="mt-3">{t('RESET_PASSWORD.PASSWORD_CHANGED')}</h5>
								<p className="text-light">{t('RESET_PASSWORD.REDIRECT_TO_HOME')}</p>
							</motion.div>
						</div>
					)}
				</main>

				<motion.div
					initial={{ opacity: 0, transform: 'translateY(-50px)' }}
					animate={{ opacity: 1, transform: 'translateY(0px)' }}
					transition={{ delay: 0.5, duration: 0.5 }}>
					<footer className="p-3 text-center" style={{ marginTop: 50 }}>
						{t('COMMON.POWERED_BY')} <span className="fw-bold">{t('COMMON.TAKHAROJ_FINANCE')}</span> {new Date().getFullYear()}{' '}
						{t('COMMON.HEFTH_PLATFORM')}.
					</footer>
				</motion.div>
			</Flex>
		</>
	);
}

export default BusinessResetPasswordPage;
